import { isEmpty, isNotEmpty } from "../../helpers/utils";
import { useGridContainer } from "../../hooks/useGridContainer";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { AccordionHeader } from "../accordionHeader/AccordionHeader";
import { AccordionHeaderText } from "../accordionHeader/AccordionHeaderText";
import { MuiBox } from "../box/MuiBox";
import { ChipOptionsDataType } from "../chip/MuiChip";
import { MuiDoubleLabel } from "../doubleLabel/MuiDoubleLabel";
import {
  MuiDoubleLabelWithChip,
  MuiDoubleLabelWithChipProps,
} from "../doubleLabelWithChip/MuiDoubleLabelWithChip";
import { MuiGrid } from "../grid/MuiGrid";
import { MuiTypography } from "../typography/MuiTypography";
import { ViewerWithCollapsibleHeaderWrapper } from "./ViewerWithCollapsibleHeader.style";

export interface ListItem extends Omit<MuiDoubleLabelWithChipProps, "value"> {
  isLabelFromCommon?: boolean;
  value: string | ChipOptionsDataType[] | React.ReactNode;
}

export interface SectionData {
  data: ListItem[];
  title: string;
  labelResolver: (key: string) => string;
  accordionKey: string;
}

interface ViewerWithCollapsibleHeaderProps {
  accordionTitle: string;
  items: ListItem[];
  isExpanded: boolean;
  labelResolver: (childKey: string) => string;
  headerTitle?: string;
  onAccordionChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  showMoreARIALabel: string;
  removeChipNameARIALabel: string;
  closePopoverARIALabel: string;
}

export const ViewerWithCollapsibleHeader = ({
  accordionTitle,
  items,
  isExpanded,
  headerTitle,
  onAccordionChange,
  labelResolver,
  showMoreARIALabel,
  removeChipNameARIALabel,
  closePopoverARIALabel,
}: ViewerWithCollapsibleHeaderProps) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { isMobile, isTablet } = useMediaQueries();

  return (
    <ViewerWithCollapsibleHeaderWrapper>
      <AccordionHeader
        showExpandCollapseIcon={true}
        title={<AccordionHeaderText label={accordionTitle} />}
        onChange={onAccordionChange}
        isExpanded={isExpanded}
      >
        <MuiBox className="container-box">
          {headerTitle && isNotEmpty(headerTitle) && (
            <MuiTypography variant="h2Bold" className="container-header">
              {headerTitle}
            </MuiTypography>
          )}
          <MuiGrid
            container
            spacing={isMobile || isTablet ? 0 : 2}
            rowSpacing={isMobile || isTablet ? 0 : 5}
            className="label-value-container"
          >
            {items.map((item, index) => (
              <>
                <MuiGrid
                  key={item.label}
                  container
                  size={
                    items.length === 1
                      ? { xs: 12, md: 12, sm: 12, lg: 12 }
                      : gridSize
                  }
                  className={gridSizeMediumClass}
                >
                  {Array.isArray(item.value) ? (
                    <MuiDoubleLabelWithChip
                      label={
                        item?.isLabelFromCommon
                          ? item?.label
                          : labelResolver(item.label)
                      }
                      value={item.value}
                      labelIcon={item?.labelIcon}
                      replaceEmptyWithDash={
                        item?.replaceEmptyWithDash ?? isEmpty(item.value)
                      }
                      isValueWithEllipsis={item?.isValueWithEllipsis}
                      chipPopoverHeaderLabel={item?.chipPopoverHeaderLabel}
                      chipPopoverGridRef={item?.chipPopoverGridRef}
                      showMoreARIALabel={showMoreARIALabel}
                      removeChipNameARIALabel={removeChipNameARIALabel}
                      closePopoverARIALabel={closePopoverARIALabel}
                    />
                  ) : (
                    <MuiDoubleLabel
                      label={
                        item?.isLabelFromCommon
                          ? item?.label
                          : labelResolver(item.label)
                      }
                      value={item.value}
                      labelIcon={item?.labelIcon}
                      replaceEmptyWithDash={
                        item?.replaceEmptyWithDash ?? isEmpty(item.value)
                      }
                      isValueWithEllipsis={item?.isValueWithEllipsis}
                    />
                  )}
                </MuiGrid>
              </>
            ))}
          </MuiGrid>
        </MuiBox>
      </AccordionHeader>
    </ViewerWithCollapsibleHeaderWrapper>
  );
};
